import { useCallback, useRef, useState } from "react";
import {
  postInteractionSubmit,
  type InteractionSubmitPayload,
  type InteractionType,
} from "../axios/interaction";
import type { Message, TabbedMessageContent } from "../types";
import {
  normalizeContentBlock,
  normalizeTab,
  findTabIndexForUpdate,
  resolveTabIndexByUiId,
  toBackendTabId,
  ensureUniqueTabIds,
} from "../components/chat/chat-bubble/tabbed-message/normalize";
import { useAppStore } from "../stores/appStore";
import { useChatStore } from "../stores/chatStore";
import {
  isBrowserOffline,
  notifyOfflineActionBlocked,
} from "../utils/internetConnection";

type SubmitArgs = {
  messageId: string;
  userId: string;
  tabId: string;
  interactionType: InteractionType;
  blockIndex: number;
  data?: string;
  payload: InteractionSubmitPayload;
};

function mergeUpdatedTabInMessages(
  messages: Message[],
  messageId: string,
  updatedTab: TabbedMessageContent,
): Message[] {
  const normalizedTab = normalizeTab(
    updatedTab as unknown as Record<string, unknown>,
  );
  return messages.map((msg) => {
    if (msg.message_id !== messageId || !msg.tabbed_content?.length) {
      return msg;
    }
    const tabIndex = findTabIndexForUpdate(msg.tabbed_content, normalizedTab);
    if (tabIndex < 0) return msg;

    const tabbed_content = msg.tabbed_content.map((tab, index) =>
      index === tabIndex ? normalizedTab : tab,
    );
    return { ...msg, tabbed_content: ensureUniqueTabIds(tabbed_content) };
  });
}

function mergeUpdatedBlockInMessages(
  messages: Message[],
  messageId: string,
  tabId: string,
  blockIndex: number,
  updatedBlock: unknown,
): Message[] {
  const normalizedBlock = normalizeContentBlock(updatedBlock);
  if (!normalizedBlock) return messages;

  return messages.map((msg) => {
    if (msg.message_id !== messageId || !msg.tabbed_content?.length) {
      return msg;
    }

    const tabIndex = resolveTabIndexByUiId(msg.tabbed_content, tabId);
    if (tabIndex < 0) return msg;

    const tabbed_content = msg.tabbed_content.map((tab, index) => {
      if (index !== tabIndex || !tab.content_blocks?.length) return tab;
      const content_blocks = tab.content_blocks.map((block, blockIdx) =>
        blockIdx === blockIndex ? normalizedBlock : block,
      );
      return { ...tab, content_blocks };
    });

    return { ...msg, tabbed_content: ensureUniqueTabIds(tabbed_content) };
  });
}

export function useInteractiveSubmit(onQuizAnswered?: () => void) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const submitInFlightRef = useRef(false);
  const { isBrowserLocked, setIsBrowserLocked, isOnline } = useAppStore();
  const setMessages = useChatStore((s) => s.setMessages);
  const isOffline = !isOnline || isBrowserOffline();

  const submit = useCallback(
    async (args: SubmitArgs) => {
      // Sync ref blocks double-clicks before React re-renders with isSubmitting.
      if (
        submitInFlightRef.current ||
        hasSubmitted ||
        isBrowserLocked ||
        isSubmitting
      ) {
        return false;
      }
      if (!isOnline || isBrowserOffline()) {
        notifyOfflineActionBlocked();
        return false;
      }

      submitInFlightRef.current = true;
      setIsSubmitting(true);
      setIsBrowserLocked(true);

      try {
        const response = await postInteractionSubmit({
          user_id: args.userId,
          message_id: args.messageId,
          tab_id: toBackendTabId(args.tabId),
          interaction_type: args.interactionType,
          block_index: args.blockIndex,
          ...(typeof args.data === "string" ? { data: args.data } : {}),
          payload: args.payload,
        });

        if (response.updated_tab) {
          setMessages((prev) =>
            mergeUpdatedTabInMessages(
              prev,
              args.messageId,
              response.updated_tab!,
            ),
          );
        } else if (response.updated_block) {
          setMessages((prev) =>
            mergeUpdatedBlockInMessages(
              prev,
              args.messageId,
              args.tabId,
              args.blockIndex,
              response.updated_block,
            ),
          );
        }

        // Keep locked after success (same as quiz buttons): unlock comes from
        // socket `is_browser_locked`. Local hasSubmitted blocks re-submit if
        // that unlock arrives before the disable/state update.
        setHasSubmitted(true);
        onQuizAnswered?.();
        return true;
      } catch (error) {
        console.error("Error submitting interaction:", error);
        submitInFlightRef.current = false;
        setIsBrowserLocked(false);
        return false;
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      hasSubmitted,
      isBrowserLocked,
      isOnline,
      isSubmitting,
      onQuizAnswered,
      setIsBrowserLocked,
      setMessages,
    ],
  );

  return {
    submit,
    isSubmitting,
    hasSubmitted,
    isBrowserLocked,
    isOffline,
  };
}
